<template>
  <SubscriptionNavigation
    @submit-clicked="onSubmit"
    :is-submitting-data="isSaveSubscriptionInProgress"
    :is-loading-data="isGetSubscriptionInProgress"
  >
    <div data-cy="equipments-page">
      <div class="text-center">
        <AtomText
          :text="t('subscription.equipments.title')"
          font-style="font-semibold"
          font-size="text-xl"
          data-cy="equipments-page-title"
        />
      </div>

      <AtomText
        :text="t('subscription.equipments.yourSituation')"
        font-style="font-semibold"
        font-size="text-lg"
        data-cy="equipments-page-your-situation-title"
        class="my-8"
      />

      <div data-cy="has-solar-panels">
        <MoleculeLabel
          label-for="hasSolarPanels"
          :text="t('subscription.equipments.form.solarPanels.label')"
          class="mb-1"
        />
        <OrganismRadioGroupCard
          v-model="hasSolarPanels"
          :cards="hasSolarPanelsChoices"
          name="hasSolarPanels"
          :error-message="errors?.hasSolarPanels"
          :has-error="isSubmitted && !!errors?.hasSolarPanels"
          data-cy="has-solar-panels-radio-group-cards"
          :disabled="isFormDisabled"
        />
      </div>

      <div data-cy="has-robin-device" class="mt-6">
        <MoleculeLabel
          label-for="hesRobinDevice"
          :text="t('subscription.equipments.form.robinDevice.label')"
          class="mb-1"
        />
        <OrganismRadioGroupCard
          v-model="hasRobinDevice"
          :cards="hasRobinDeviceChoices"
          name="hesRobinDevice"
          :error-message="errors?.hasRobinDevice"
          :has-error="isSubmitted && !!errors?.hasRobinDevice"
          data-cy="has-robin-device-radio-group-cards"
          :disabled="isFormDisabled"
        />
        <MoleculeStandaloneLink
          :label="t('subscription.equipments.form.robinDevice.helperLinkLabel')"
          to=""
          color-variant="brand"
          icon-name="QuestionMarkCircleIcon"
          :is-icon-left="true"
          type="route"
          target="blank"
          font-size="sm"
          font-weight="medium"
          class="mt-1"
          @click="handleRobinDeviceHelperLinkClick"
        />
        <OrganismAlert
          v-if="hasRobinDevice === EEquipmentInstallationStatus.NOT_EQUIPPED"
          variant="complex"
          :title="t('subscription.equipments.robinDeviceAlertInfo.title')"
          :content-html="t('subscription.equipments.robinDeviceAlertInfo.description')"
          class="mt-2"
          data-cy="robin-device-alert-info"
          state="info"
        />
      </div>

      <AtomDivider class="my-10" />

      <AtomText
        :text="t('subscription.equipments.yoursEquipments')"
        font-style="font-semibold"
        font-size="text-lg"
        data-cy="equipments-page-your-equipments-title"
        class="mb-8"
      />

      <div data-cy="has-physical-battery">
        <MoleculeLabel
          label-for="hasPhysicalBattery"
          :text="t('subscription.equipments.form.physicalBattery.label')"
          class="mb-1"
        />
        <OrganismRadioGroupCard
          v-model="hasPhysicalBattery"
          :cards="hasPhysicalBatteryChoices"
          name="hasPhysicalBattery"
          :error-message="errors?.hasPhysicalBattery"
          :has-error="isSubmitted && !!errors?.hasPhysicalBattery"
          data-cy="has-physical-battery-radio-group-cards"
          :disabled="isFormDisabled"
        />
      </div>

      <div data-cy="has-my-light-150-ev-charger" class="mt-6">
        <MoleculeLabel
          label-for="hasMyLight150ElectricVehicleCharger"
          :text="t('subscription.equipments.form.evCharger.label')"
          class="mb-1"
        />
        <OrganismRadioGroupCard
          v-model="hasMyLight150ElectricVehicleCharger"
          :cards="hasMyLight150ElectricVehicleChargerChoice"
          name="hasMyLight150ElectricVehicleCharger"
          :error-message="errors?.hasMyLight150ElectricVehicleCharger"
          :has-error="isSubmitted && !!errors?.hasMyLight150ElectricVehicleCharger"
          data-cy="has-my-light-150-ev-charger-radio-group-cards"
          :disabled="isFormDisabled"
        />
      </div>

      <div data-cy="main-heating-source" class="mt-6">
        <MoleculeLabel
          label-for="mainHeatingSource"
          :text="t('subscription.equipments.form.mainHeatingSource.label')"
          class="mb-1"
        />
        <OrganismRadioGroupCard
          v-model="mainHeatingSource"
          :cards="mainHeatingSourceChoices"
          :error-message="errors?.mainHeatingSource"
          :has-error="isSubmitted && !!errors?.mainHeatingSource"
          name="mainHeatingSource"
          data-cy="main-heating-source-radio-group-cards"
          :disabled="isFormDisabled"
          orientation="horizontal"
        >
          <template #card-main-heating-source-AirToAirHeatPump>
            <AtomIcon icon-name="AirToAirHeatPumpIcon" iconType="outline" />
          </template>

          <template #card-main-heating-source-AirToWaterHeatPump>
            <AtomIcon icon-name="AirToWaterHeatPumpIcon" iconType="outline" />
          </template>

          <template #card-main-heating-source-ElectricRadiators>
            <AtomIcon icon-name="LightningBoltIcon" iconType="outline" />
          </template>

          <template #card-main-heating-source-NoElectric>
            <AtomIcon icon-name="FireIcon" iconType="outline" />
          </template>
        </OrganismRadioGroupCard>
      </div>

      <div data-cy="water-heating-source" class="mt-6">
        <MoleculeLabel
          label-for="waterHeatingSource"
          :text="t('subscription.equipments.form.waterHeatingSource.label')"
          class="mb-1"
        />
        <OrganismRadioGroupCard
          v-model="waterHeatingSource"
          :cards="waterHeatingSourceChoices"
          :error-message="errors?.waterHeatingSource"
          :has-error="isSubmitted && !!errors?.waterHeatingSource"
          name="waterHeatingSource"
          data-cy="water-heating-source-radio-group-cards"
          :disabled="isFormDisabled"
          orientation="horizontal"
        >
          <template #card-water-heating-source-HeatPump>
            <AtomIcon icon-name="PacIcon" iconType="outline" />
          </template>

          <template #card-water-heating-source-ElectricWaterHeater>
            <AtomIcon icon-name="WaterHeaterIcon" iconType="outline" />
          </template>

          <template #card-water-heating-source-ThermodynamicWaterHeater>
            <AtomIcon icon-name="ThermodynamicIcon" iconType="outline" />
          </template>

          <template #card-water-heating-source-NoElectric>
            <AtomIcon icon-name="FireIcon" iconType="outline" />
          </template>
        </OrganismRadioGroupCard>
      </div>

      <div data-cy="other-equipments" class="mt-6">
        <MoleculeLabel
          label-for="otherEquipments"
          :text="t('subscription.equipments.form.otherEquipments.label')"
          :optional-text="t('common.forms.optional')"
          class="mb-1"
        />
        <OrganismCheckboxGroupCard
          v-model="otherEquipments"
          :cards="otherEquipmentsChoices"
          data-cy="mlh-equipments-checkbox-group-cards"
          :disabled="isFormDisabled"
          orientation="horizontal"
        >
          <template #card-other-equipment-AirConditioning>
            <AtomIcon icon-name="AirConditionerIcon" iconType="outline" />
          </template>

          <template #card-other-equipment-Fireplace>
            <AtomIcon icon-name="FireIcon" iconType="outline" />
          </template>

          <template #card-other-equipment-SwimmingPool>
            <AtomIcon icon-name="InGroundPoolIcon" iconType="outline" />
          </template>

          <template #card-other-equipment-ElectricalVehicle>
            <AtomIcon icon-name="ElectricVehicleIcon" iconType="outline" />
          </template>

          <template #card-other-equipment-HybridVehicle>
            <AtomIcon icon-name="CarIcon" iconType="outline" />
          </template>

          <template #card-other-equipment-Spa>
            <AtomIcon icon-name="JacuzziIcon" iconType="outline" />
          </template>
        </OrganismCheckboxGroupCard>
      </div>
    </div>

    <RobinDeviceHelperModal v-model:is-open="isRobinDeviceHelperModalOpen" />
  </SubscriptionNavigation>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  useEquipmentsDeclarationStore,
  useStepStore,
  useSubscriptionStore,
  useToastSnackbarStore,
} from '@/stores';

import type { RgvRadioGroupCard } from '@/types/regenerativ.type';
import type { SubscriptionProfile } from '@/types/subscription.type';
import { getErrorToasterConfig } from '@/utils/error.util';

import RobinDeviceHelperModal from '@/components/RobinDeviceHelperModal.component.vue';
import SubscriptionNavigation from '@/components/SubscriptionNavigation.component.vue';

import {
  EEquipmentInstallationStatus,
  EMainHeatingSource,
  EOtherEquipment,
  EWaterHeatingSource,
} from '@/constants/subscription.constants';

const { t } = useI18n();

const toastSnackBar = useToastSnackbarStore();
const subscriptionStore = useSubscriptionStore();
const equipmentsDeclarationStore = useEquipmentsDeclarationStore();
const stepStore = useStepStore();

const { goToNextStep } = stepStore;

const {
  hasSolarPanels,
  hasRobinDevice,
  hasPhysicalBattery,
  hasMyLight150ElectricVehicleCharger,
  mainHeatingSource,
  waterHeatingSource,
  otherEquipments,

  errors,
} = storeToRefs(equipmentsDeclarationStore);

const { isGetSubscriptionInProgress, isGetSubscriptionSuccessfully, isSaveSubscriptionInProgress } =
  storeToRefs(subscriptionStore);

const buildEquipmentChoices = (idPrefix: string): RgvRadioGroupCard[] => {
  return Object.values(EEquipmentInstallationStatus).map(status => ({
    id: `${idPrefix}-${status}`,
    title:
      status === EEquipmentInstallationStatus.ALREADY_EQUIPPED
        ? t(`subscription.equipments.choices.${status}.${idPrefix}`)
        : t(`subscription.equipments.choices.${status}`),
    value: status,
  }));
};

const hasSolarPanelsChoices = ref<RgvRadioGroupCard[]>(buildEquipmentChoices('solarPanels'));

const hasRobinDeviceChoices = ref<RgvRadioGroupCard[]>(buildEquipmentChoices('robinDevice'));

const hasPhysicalBatteryChoices = ref<RgvRadioGroupCard[]>(
  buildEquipmentChoices('physicalBattery')
);

const hasMyLight150ElectricVehicleChargerChoice = ref<RgvRadioGroupCard[]>(
  buildEquipmentChoices('evCharger')
);

const mainHeatingSourceChoices = ref<RgvRadioGroupCard[]>(
  Object.values(EMainHeatingSource).map(source => ({
    id: `main-heating-source-${source}`,
    title: t(`subscription.equipments.options.mainHeatingSource.${source}.title`),
    value: source,
    description:
      source !== EMainHeatingSource.ElectricRadiators
        ? t(`subscription.equipments.options.mainHeatingSource.${source}.description`)
        : '',
  }))
);

const waterHeatingSourceChoices = ref<RgvRadioGroupCard[]>(
  Object.values(EWaterHeatingSource).map(source => ({
    id: `water-heating-source-${source}`,
    title: t(`subscription.equipments.options.waterHeatingSource.${source}.title`),
    value: source,
    description: t(`subscription.equipments.options.waterHeatingSource.${source}.description`),
  }))
);

const otherEquipmentsChoices = ref<RgvRadioGroupCard[]>(
  Object.values(EOtherEquipment).map((equipment): RgvRadioGroupCard => ({
    id: `other-equipment-${equipment}`,
    title: t(`subscription.equipments.options.otherEquipments.${equipment}.title`),
    value: equipment,
  }))
);

const isSubmitted = ref(false);

const isRobinDeviceHelperModalOpen = ref(false);

const isFormDisabled = computed(
  () => isGetSubscriptionInProgress.value || isSaveSubscriptionInProgress.value
);

const handleRobinDeviceHelperLinkClick = () => {
  isRobinDeviceHelperModalOpen.value = true;
};

const getProfileData = (): SubscriptionProfile => ({
  hasSolarPanels: hasSolarPanels.value,
  hasRobinDevice: hasRobinDevice.value,
  hasPhysicalBattery: hasPhysicalBattery.value,
  hasMyLight150ElectricVehicleCharger: hasMyLight150ElectricVehicleCharger.value,
  mainHeatingSource: mainHeatingSource.value,
  waterHeatingSource: waterHeatingSource.value,
  otherEquipments: otherEquipments.value || [],
});

const onSubmit = async () => {
  isSubmitted.value = true;

  const installationEquipmentsValidation =
    await equipmentsDeclarationStore.validateInstallationEquipments();

  if (!installationEquipmentsValidation.valid) {
    return;
  }

  try {
    const payload = getProfileData();

    await subscriptionStore.updateSubscriptionWithProfile(payload);

    goToNextStep();
  } catch (error) {
    const toastConfig = getErrorToasterConfig({
      error,
      toasterId: 'equipments-configuration',
      defaultMessage: t('subscription.equipments.api.errors.patchSubscriptionConsumption'),
    });

    toastSnackBar.addToast(toastConfig);
  }
};

watch(isGetSubscriptionSuccessfully, isSuccessfully => {
  if (isSuccessfully) {
    equipmentsDeclarationStore.setDefautValues();
  }
});

onMounted(() => {
  equipmentsDeclarationStore.setDefautValues();
});
</script>

<style scoped></style>
